// Devices — 真實資料版（Route 11, PR #101）。

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Smartphone, RefreshCw, ServerCrash, Inbox, Building2, Ban, KeyRound, CheckCircle2 } from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import DataTable, { type Column } from "../components/DataTable.js";
import PaginationControl from "../components/PaginationControl.js";
import { usePagination } from "../lib/usePagination.js";
import EmptyState from "../components/EmptyState.js";
import TenantPickerEmptyState from "../components/TenantPickerEmptyState.js";
import Skeleton from "../components/Skeleton.js";
import {
  listAdminDevices, patchDeviceStatus, friendlyDevicesError,
  type DeviceRow, type DeviceStatus,
} from "../lib/devicesApi.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { useConfirmDialog } from "../components/ConfirmDialog.js";
import { formatTaipeiAgo } from "../../lib/time.js";

const STATUS_BADGE: Record<DeviceStatus, string> = { active: "badge-success", revoked: "badge-danger" };
const STATUS_LABEL: Record<DeviceStatus, string> = { active: "啟用", revoked: "已停用" };

type ListState =
  | { status: "loading" }
  | { status: "ready"; rows: DeviceRow[]; total: number }
  | { status: "error"; message: string };

export default function Devices(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const canWrite = session.scopes.includes("write:user") || session.super_admin === true;
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [busy, setBusy] = useState<string | null>(null);
  const dlg = useConfirmDialog();
  const pag = usePagination({ urlPrefix: "dev_" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const r = await listAdminDevices({ tenantId: tenantParam, limit: pag.limit, offset: pag.offset });
      setState({ status: "ready", rows: r.items, total: r.total });
    } catch (err) { setState({ status: "error", message: friendlyDevicesError(err) }); }
  }, [tenantParam, pag.limit, pag.offset]);
  useEffect(() => { void load(); }, [load]);

  const toggle = async (d: DeviceRow) => {
    const target: DeviceStatus = d.status === "active" ? "revoked" : "active";
    const verb = target === "revoked" ? "停用" : "重新啟用";
    if (!(await dlg.confirm(`確定要${verb}「${d.officer_name} 的 ${d.device_model ?? "裝置"}」？`, { destructive: target === "revoked", okLabel: verb }))) return;
    setBusy(d.id);
    try { await patchDeviceStatus(d.id, target, tenantParam); await load(); }
    catch (e) { await dlg.alert(friendlyDevicesError(e), { title: "操作失敗" }); } finally { setBusy(null); }
  };

  const columns: Column<DeviceRow>[] = useMemo(() => [
    {
      key: "device", header: "裝置", primary: true, width: "30%",
      render: (d) => (
        <div className="flex items-center gap-2">
          <Smartphone className="h-4 w-4 text-text-muted flex-shrink-0" />
          <div>
            <div className="font-medium text-text-primary">{d.device_model ?? "未命名裝置"}</div>
            <div className="text-2xs text-text-muted">{d.os ?? d.user_agent?.slice(0, 40) ?? "—"}</div>
          </div>
        </div>
      ),
    },
    { key: "officer", header: "巡邏員", render: (d) => <span className="text-text-secondary">{d.officer_name}</span> },
    {
      key: "webauthn", header: "Passkey",
      render: (d) => d.webauthn_bound
        ? <span className="badge-success inline-flex items-center gap-1"><KeyRound className="h-3 w-3" />已綁定</span>
        : <span className="badge-default">未綁定</span>,
    },
    { key: "iphash", header: "IP hash", hideOnMobile: true,
      render: (d) => <span className="font-mono text-2xs text-text-muted">{d.last_ip_hash ?? "—"}</span> },
    { key: "lastSeen", header: "最後上線",
      render: (d) => <span className="font-mono text-text-secondary text-xs">{formatTaipeiAgo(d.last_seen_at)}</span> },
    { key: "status", header: "狀態",
      render: (d) => <span className={STATUS_BADGE[d.status]}>{STATUS_LABEL[d.status]}</span> },
    {
      key: "actions", header: "", width: "1%",
      render: (d) => canWrite ? (
        <button className="btn-ghost text-xs h-7" onClick={(e) => { e.stopPropagation(); void toggle(d); }} disabled={busy === d.id}>
          {d.status === "active" ? <><Ban className="h-3.5 w-3.5" />停用</> : <><CheckCircle2 className="h-3.5 w-3.5" />啟用</>}
        </button>
      ) : null,
    },
  ], [canWrite, busy]);

  if (session.super_admin && !tenantParam) {
    return (<><PageHeader title="裝置" description="巡邏員已綁定的 Passkey 裝置" />
      <TenantPickerEmptyState /></>);
  }

  return (
    <>
      <PageHeader
        title="裝置"
        description="巡邏員已綁定的 Passkey 裝置與最後上線狀態"
        actions={
          <button className="btn-secondary text-sm" onClick={() => void load()} disabled={state.status === "loading"}>
            <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
          </button>
        }
      />
      {state.status === "loading" && <ListSkeleton />}
      {state.status === "error" && <EmptyState icon={ServerCrash} title="裝置列表載入失敗" description={state.message}
        action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />}
      {state.status === "ready" && state.rows.length === 0 && (
        <EmptyState icon={Smartphone} title="這個單位還沒有任何裝置" description="請到「人員」頁產生「裝置註冊」連結，巡邏員在自己的手機完成 Passkey 註冊後，裝置就會出現在這裡。" />
      )}
      {state.status === "ready" && state.rows.length > 0 && (
        <>
          <DataTable rows={state.rows} columns={columns} rowKey={(d) => d.id}
            searchableText={(d) => `${d.device_model ?? ""} ${d.os ?? ""} ${d.officer_name} ${d.last_ip_hash ?? ""}`}
            serverPagination
          />
          <div className="card mt-3">
            <PaginationControl
              page={pag.page}
              pageSize={pag.limit}
              total={state.total}
              onPageChange={pag.setPage}
              onPageSizeChange={pag.setLimit}
            />
          </div>
        </>
      )}
    </>
  );
}

function ListSkeleton(): React.ReactElement {
  return (
    <div className="card overflow-hidden">
      <div className="p-4 border-b border-border"><Skeleton className="h-9 w-72" /></div>
      <div className="divide-y divide-border">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-4 p-4">
            <Skeleton className="h-4 w-4 rounded" />
            <div className="flex-1 space-y-1.5"><Skeleton className="h-4 w-1/3" /><Skeleton className="h-3 w-1/2" /></div>
            <Skeleton className="h-5 w-16" />
            <Skeleton className="h-5 w-12 hidden sm:block" />
          </div>
        ))}
      </div>
    </div>
  );
}
